import { CustomFontFamily, CustomFontStyle } from "../brand-kit";
import { FontFamily } from "../element-schema";

const DEFAULT_FALLBACK = "Arial, sans-serif";

/** A client without the font falls back to one of the same kind, not to Arial. */
const SYSTEM_FONT_FALLBACKS: Partial<Record<string, string>> = {
  [FontFamily.GEORGIA]: "'Times New Roman', serif",
  [FontFamily.TIMES_NEW_ROMAN]: "Georgia, serif",
  [FontFamily.COURIER_NEW]: "monospace",
  [FontFamily.PACIFICO]: "cursive",
};

function withSystemFallback(fontFamily: string): string {
  if (fontFamily.includes(",")) {
    return fontFamily;
  }
  return `${fontFamily}, ${SYSTEM_FONT_FALLBACKS[fontFamily] ?? DEFAULT_FALLBACK}`;
}

const CSS_IDENTIFIER_UNSAFE = /[^a-zA-Z0-9\-_]/;

/**
 * Quotes a font family name for safe use in CSS values (inline styles, @font-face, FontFace API).
 * Names containing characters that aren't valid in CSS identifiers (e.g. apostrophes, spaces)
 * must be quoted to avoid silent rejection by the CSS parser.
 */
export function cssFontFamily(name: string): string {
  if (!CSS_IDENTIFIER_UNSAFE.test(name)) {
    return name;
  }
  const escaped = name.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  // We use single quotes here just to be safe, but either quote style works. Previously
  // html-minifier's minifyCSS option couldn't parse double quotes in HTML attributes;
  // we no longer use minifyCSS (see email-ssr/render-pipeline.ts) so it could be either.
  return `'${escaped}'`;
}

/**
 * Checks if a font family is a custom font
 */
export function isCustomFont(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): boolean {
  if (!customFontFamilies) {
    return false;
  }
  return customFontFamilies.some((cf) => cf.fontFamily === fontFamily);
}

/**
 * Gets a custom font family object by name
 */
export function getCustomFontFamily(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): CustomFontFamily | undefined {
  if (!customFontFamilies) {
    return undefined;
  }
  return customFontFamilies.find((cf) => cf.fontFamily === fontFamily);
}

/**
 * Gets the appropriate font-family CSS value with fallbacks.
 * For custom fonts, this returns the custom font with its specific fallback.
 * For system fonts, this returns the font with standard web-safe fallbacks.
 */
export function getFontFamilyWithFallback(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): string {
  if (!fontFamily) {
    return DEFAULT_FALLBACK;
  }

  if (!customFontFamilies) {
    return withSystemFallback(fontFamily);
  }

  const customFont = customFontFamilies.find(
    (cf) => cf.fontFamily === fontFamily,
  );

  if (customFont) {
    return `${cssFontFamily(customFont.fontFamily)}, ${customFont.fallbackFont}`;
  }

  return withSystemFallback(fontFamily);
}

/**
 * Quotes each family in a `font-family` value. Quoting the whole string would
 * turn a fallback list into one absurd family name, so the split comes first.
 */
export function cssFontFamilyList(value: string): string {
  return value
    .split(",")
    .map((family) => cssFontFamily(family.trim()))
    .filter(Boolean)
    .join(", ");
}

/**
 * Inverse of {@link cssFontFamilyList}: browsers hand back whatever quoting
 * they feel like, so strip it to compare against a stored family name.
 */
export function parseCssFontFamilyList(value: string): string {
  return value
    .split(",")
    .map((family) => unquoteCssFontFamily(family.trim()))
    .filter(Boolean)
    .join(", ");
}

function unquoteCssFontFamily(family: string): string {
  const quote = family[0];
  if ((quote !== "'" && quote !== '"') || family.at(-1) !== quote) {
    return family;
  }
  return family
    .slice(1, -1)
    .replace(/\\(['"\\])/g, "$1")
    .trim();
}

/**
 * The `@font-face` styles a set of family names needs, skipping families the
 * browser already has. Order follows the request and duplicates collapse, so
 * callers can pass overlapping sources (page typography, per-block, per-line)
 * without pre-deduping.
 */
export function customFontStylesToLoad(
  fontFamilies: readonly (string | undefined)[],
  customFontFamilies: CustomFontFamily[] | null | undefined,
): { fontFamily: string; style: CustomFontStyle }[] {
  const seen = new Set<string>();
  const result: { fontFamily: string; style: CustomFontStyle }[] = [];
  for (const fontFamily of fontFamilies) {
    if (!fontFamily) {
      continue;
    }
    const font = customFontFamilies?.find((f) => f.fontFamily === fontFamily);
    if (!font) {
      continue;
    }
    for (const style of font.styles) {
      const key = `${fontFamily}-${style._id}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ fontFamily: font.fontFamily, style });
      }
    }
  }
  return result;
}
